import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  RightOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Checkbox,
  CheckboxChangeEvent,
  Divider,
  Flex,
  Grid,
  Statistic,
} from "antd";
import dayjs from "dayjs";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { H6, P1, P2, P3 } from "../../../../components/NormalFonts";
import {
  OrderDetailsType,
  OrderListResponse,
} from "../../../../hooks/Order/data";
import { SkiResortResponse } from "../../../../hooks/Resorts/data";
import useGetTimezone from "../../../../hooks/Timezone/timezone.hooks";
import { moneyFormat } from "../../../../plugins/numberFormat";
import { envChangeType, envType } from "../../../../plugins/utils";
import { orderType } from "../../enumerations/orderType";
import { paymentStatus } from "../../enumerations/paymentStatus";
import { RecordItemProps } from "./data";
import styles from "./styles.module.scss";

const RecordItem: React.FC<RecordItemProps> = ({
  item,
  resortList,
  selectedItem,
  setSelectedItem,

  selectedOrders,
  setSelectedOrders,
  handleCancel,
  handleCheckout,
  handleProcessRefund,
  handleRefund,
  handleClickDetail,
  loading,
}) => {
  const { t } = useTranslation();
  const { toServerTimezone, toLocalTimezone, formatDate, forceDate } =
    useGetTimezone();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const { Countdown } = Statistic;

  const paymentStatusEnum: Record<string, { color: string; icon: ReactNode }> =
    {
      [paymentStatus.NOT_PAY]: {
        color: "var(--warn)",
        icon: (
          <WarningOutlined style={{ fontSize: 20, color: "var(--warn)" }} />
        ),
      },
      [paymentStatus.PAY_PROCESS]: {
        color: "var(--warn)",
        icon: (
          <WarningOutlined style={{ fontSize: 20, color: "var(--warn)" }} />
        ),
      },
      [paymentStatus.PAY_SUCCESS]: {
        color: "var(--success)",
        icon: (
          <CheckCircleOutlined
            style={{ fontSize: 20, color: "var(--success)" }}
          />
        ),
      },
      [paymentStatus.PAY_FAILED]: {
        color: "var(--error)",
        icon: (
          <CloseCircleOutlined
            style={{ fontSize: 20, color: "var(--error)" }}
          />
        ),
      },
      [paymentStatus.REFUND]: {
        color: "var(--info)",
        icon: (
          <CheckCircleOutlined style={{ fontSize: 20, color: "var(--info)" }} />
        ),
      },
      [paymentStatus.PARTIAL_REFUND]: {
        color: "var(--info)",
        icon: (
          <CheckCircleOutlined style={{ fontSize: 20, color: "var(--info)" }} />
        ),
      },
      [paymentStatus.REFUND_PROCESS]: {
        color: "var(--warn)",
        icon: (
          <WarningOutlined style={{ fontSize: 20, color: "var(--warn)" }} />
        ),
      },
      [paymentStatus.REFUND_FAILED]: {
        color: "var(--error)",
        icon: (
          <CloseCircleOutlined
            style={{ fontSize: 20, color: "var(--error)" }}
          />
        ),
      },
      [paymentStatus.REFUND_APPLY]: {
        color: "var(--warn)",
        icon: (
          <CloseCircleOutlined style={{ fontSize: 20, color: "var(--warn)" }} />
        ),
      },
      [paymentStatus.CANCEL]: {
        color: "var(--empty)",
        icon: (
          <WarningOutlined style={{ fontSize: 20, color: "var(--empty)" }} />
        ),
      },
    };
  const paymentSmallOrderStatusEnum: Record<
    string,
    { color: string; backgroundColor: string }
  > = {
    [paymentStatus.REFUND]: {
      color: "var(--info)",
      backgroundColor: "#D6ECFF",
    },
    [paymentStatus.PARTIAL_REFUND]: {
      color: "var(--info)",
      backgroundColor: "#D6ECFF",
    },
    [paymentStatus.REFUND_PROCESS]: {
      color: "var(--warn)",
      backgroundColor: "#FFE9D6",
    },
    [paymentStatus.REFUND_APPLY]: {
      color: "var(--warn)",
      backgroundColor: "#FFE9D6",
    },
  };

  const handleOnSelectOrder = (
    e: CheckboxChangeEvent,
    detail: OrderDetailsType
  ) => {
    if (e.target.checked === true) {
      setSelectedOrders((prev) => [...prev, detail.order_number]);
    } else {
      setSelectedOrders((prevItems) =>
        prevItems.filter((item) => item !== detail.order_number)
      );
    }
  };

  const renderBtn = (item: OrderListResponse) => {
    switch (item.status) {
      case paymentStatus.NOT_PAY:
      case paymentStatus.PAY_FAILED:
        if (!item?.created_at) return;
        const isReplenishOrder = item.order_details.some(
          (_item: OrderDetailsType) =>
            _item.order_type === orderType.REPLENISH_ORDER
        );
        const now = toLocalTimezone(dayjs());
        const tomorrow = now?.add(1, "day");
        const startDate = toLocalTimezone(item?.order_details[0]?.start_date);
        // 判断 startDate 是否是明天
        const isStartDateTomorrow = startDate?.isSame(tomorrow, "day");

        const createDate = toLocalTimezone(item?.created_at);
        const countdownEnd = createDate?.add(2, "hour");
        const limitHour = envChangeType(
          "REACT_APP_LIMIT_NEXT_DAY_ORDER",
          envType.NUMBER
        );
        const sevenPmUtcPlus9 = createDate
          ?.hour(limitHour)
          .minute(0)
          .second(0)
          .millisecond(0);

        //TODO: Open when 7PM logic need to use
        const deadLine = isStartDateTomorrow
          ? countdownEnd?.isBefore(sevenPmUtcPlus9)
            ? countdownEnd
            : sevenPmUtcPlus9
          : countdownEnd;

        return (
          <Flex
            gap={8}
            vertical={!screens.sm}
            style={{
              flexDirection: screens.sm ? "row" : "column-reverse",
              width: screens.sm ? "auto" : "100%",
            }}
          >
            <Button
              loading={loading}
              danger
              type="link"
              style={{
                fontSize: "var(--fontSetting-middle)",
                height: 36,
              }}
              onClick={() => handleCancel(item)}
            >
              {t("record.cancelOrder")}
            </Button>
            {isReplenishOrder ? (
              <Button
                loading={loading}
                type="primary"
                size="small"
                onClick={() => handleCheckout(item)}
              >
                {t("button.replenish.pay")}
              </Button>
            ) : (
              <Button
                loading={loading}
                disabled={now?.isAfter(deadLine)}
                type="primary"
                style={{
                  fontSize: "var(--fontSetting-middle)",
                  height: 36,
                }}
                onClick={() => handleCheckout(item)}
              >
                {
                  <Flex align="center" gap={10}>
                    {t("record.continuePay")}
                    <Countdown
                      value={deadLine?.valueOf()}
                      // onChange={onChange}
                    />
                  </Flex>
                }
              </Button>
            )}
          </Flex>
        );
      case paymentStatus.PAY_SUCCESS: {
        if (item.has_discount === 1) return;
        if (
          item?.order_details &&
          item.order_details.length > 0 &&
          item.order_details[0].order_type === orderType.REPLENISH_ORDER
        )
          return;
        return (
          <Button
            loading={loading}
            danger
            type="link"
            style={{
              fontSize: "var(--fontSetting-middle)",
              height: 36,
            }}
            onClick={() => handleProcessRefund(item)}
          >
            {t("record.applyRefund")}
          </Button>
        );
      }
      case paymentStatus.REFUND_FAILED:
        if (item.has_discount === 1) {
          return;
        }
        return (
          <Button
            loading={loading}
            danger
            type="link"
            style={{
              fontSize: "var(--fontSetting-middle)",
              height: 36,
            }}
            onClick={() => handleProcessRefund(item)}
          >
            {t("record.reapplyRefund")}
          </Button>
        );
      default:
        break;
    }
  };

  const renderTitle = () => {
    return (
      <Flex gap={16} align="center">
        {screens.sm && (
          <P2 color="var(--text-content-2)">{t("record.orderDate")}</P2>
        )}
        <P1 weight="medium">
          {toLocalTimezone(item.created_at)?.format("YYYY-MM-DD")}
        </P1>
      </Flex>
    );
  };

  const renderExtra = () => {
    return (
      <Flex gap={8} align="center">
        <P1 weight="medium" color={paymentStatusEnum[item?.status]?.color}>
          {t(`record.paymentStatus.${item.status}`)}
        </P1>
        {paymentStatusEnum[item?.status]?.icon}
      </Flex>
    );
  };

  const renderFooter = () => {
    return (
      <Flex
        gap={screens.sm ? 44 : 16}
        align="center"
        justify="flex-end"
        vertical={!screens.sm}
        style={{
          flexDirection: screens.sm ? "row" : "column-reverse",
        }}
      >
        {renderBtn(item)}
        <Flex
          vertical
          align="flex-end"
          justify="flex-start"
          gap={16}
          style={{ alignSelf: "flex-end" }}
        >
          {selectedItem?.payment_number === item.payment_number ? null : (
            <Flex
              gap={20}
              align="center"
              justify={screens.sm ? "" : "space-between"}
              style={{ width: screens.sm ? "auto" : "100%" }}
            >
              <P2 color="var(--text-content-2)">{t("search.summary")}</P2>
              <H6 color="var(--primary)" weight="heavy">
                {`${t(
                  `currency.${process.env.REACT_APP_CURRENCY}`
                )} ${moneyFormat(item.amount)}`}
              </H6>
            </Flex>
          )}
          {[paymentStatus.PARTIAL_REFUND, paymentStatus.REFUND].includes(
            item.status
          ) ? (
            <Flex
              gap={20}
              align="center"
              justify={screens.sm ? "" : "space-between"}
              style={{ width: screens.sm ? "auto" : "100%" }}
            >
              <P2 color="var(--text-content-2)">{t("record.alreadyrefund")}</P2>
              <P2 color="var(--warn)" weight="medium">
                {`${t(
                  `currency.${process.env.REACT_APP_CURRENCY}`
                )} ${moneyFormat(item.refund_amount)}`}
              </P2>
            </Flex>
          ) : null}
        </Flex>
      </Flex>
    );
  };

  return (
    <Card
      title={renderTitle()}
      extra={renderExtra()}
      size={screens.sm ? "default" : "small"}
      styles={{
        header: {
          background: "transparent",
          borderBottom: "1px solid var(--table-divider)",
        },
      }}
    >
      {item?.order_details?.map((detail: OrderDetailsType, index: number) => {
        const targetResort = resortList?.findIndex(
          (resort: SkiResortResponse) =>
            resort.ski_resort_code === detail.ski_resort_code
        );
        if (detail.order_type === orderType.REPLENISH_ORDER) {
          return (
            <div key={index}>
              <Flex vertical={true} gap={20}>
                <Flex align="center" justify={"space-between"}>
                  <P1 color="var(--text-content-1)">
                    {`${t("checkout.data.notes.title")} : ${
                      detail?.fix_reason || "-"
                    }`}
                  </P1>
                  <Flex
                    gap={8}
                    align="center"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleClickDetail(detail)}
                  >
                    {screens.sm && (
                      <P2 color="var(--primary)" weight="medium">
                        {t("record.detail")}
                      </P2>
                    )}
                    <RightOutlined style={{ color: "var(--primary)" }} />
                  </Flex>
                </Flex>
                <Flex style={{ alignSelf: "flex-end" }} gap={20} align="center">
                  <P2 color="var(--text-content-2)">{t("record.subtotal")}</P2>
                  <P2 color="var(--text-content-2)" weight="medium">
                    {`${t(
                      `currency.${process.env.REACT_APP_CURRENCY}`
                    )} ${moneyFormat(detail.total_price)}`}
                  </P2>
                </Flex>
              </Flex>
              <Divider style={{ margin: screens.sm ? "18px 0" : "8px 0" }} />
            </div>
          );
        }
        return (
          <div key={index}>
            <Flex className={styles.order} gap={20}>
              {/* TODO: ADD LOGIC */}
              {selectedItem?.payment_number === item.payment_number ? (
                <Checkbox
                  value={selectedOrders.includes(detail.order_number)}
                  onChange={(e: CheckboxChangeEvent) =>
                    handleOnSelectOrder(e, detail)
                  }
                />
              ) : null}
              <img
                src={
                  targetResort !== -1
                    ? resortList[targetResort]?.image_url
                    : "123"
                }
                alt="ski_img"
                className={styles.image}
              />
              <Flex vertical gap={4} style={{ flex: 1 }}>
                <Flex align="center" justify="space-between">
                  <Flex align="center" gap={4}>
                    <P1 color="var(--text-content-1)" weight="medium">
                      {detail.coach_nickname
                        ? t("normal.coach.nickname", {
                            nickname: detail.coach_nickname,
                          })
                        : t(`skiresort.${detail.ski_resort_code}`)}
                    </P1>
                    {detail.payment_status &&
                    detail.payment_status !== paymentStatus.REFUND_FAILED ? (
                      <P3
                        className={styles.statusWrapper}
                        weight="medium"
                        style={{
                          background:
                            paymentSmallOrderStatusEnum[detail.payment_status]
                              .backgroundColor,
                          color:
                            paymentSmallOrderStatusEnum[detail.payment_status]
                              .color,
                        }}
                      >
                        {t(
                          `record.details.paymentStatus.${detail.payment_status}`
                        )}
                      </P3>
                    ) : null}
                  </Flex>
                  <Flex
                    gap={8}
                    align="center"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleClickDetail(detail)}
                  >
                    {screens.sm && (
                      <P2 color="var(--primary)" weight="medium">
                        {t("record.detail")}
                      </P2>
                    )}
                    <RightOutlined style={{ color: "var(--primary)" }} />
                  </Flex>
                </Flex>
                <P1 color="var(--text-content-1)">
                  {`${formatDate(
                    toLocalTimezone(detail.start_date)
                  )} - ${toLocalTimezone(detail.end_date)?.format(
                    `MM${t("normal.month")}DD${t("normal.date")}`
                  )}`}
                </P1>
                {detail.coach_nickname && (
                  <P1 color="var(--text-content-1)">
                    {t(`skiresort.${detail.ski_resort_code}`)}
                  </P1>
                )}
                {/* <P1 color="var(--text-content-1)">
                  {t("checkout.lessons", {
                    skill: t(`normal.skill.${detail.skill_code}`),
                    count: detail.session_duration || 0,
                  })}
                </P1> */}
              </Flex>
              <Flex className={styles.totalPrice} gap={20} align="center">
                <P2 color="var(--text-content-2)">{t("record.subtotal")}</P2>
                <P2 color="var(--text-content-2)" weight="medium">
                  {`${t(
                    `currency.${process.env.REACT_APP_CURRENCY}`
                  )} ${moneyFormat(detail.total_price)}`}
                </P2>
              </Flex>
            </Flex>
            <Divider style={{ margin: screens.sm ? "18px 0" : "8px 0" }} />
          </div>
        );
      })}
      {renderFooter()}
    </Card>
  );
};

export default RecordItem;
